import React, { useState } from 'react'
import { Table, TableCell, TableHead, TableRow, TableBody, Box, Card, CardContent, Grid, Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { keyBy } from 'lodash'
import SubscriberDialog from './indicatorDialogs/subscriber/SubscriberDialog'
import { formatIndicatorValue, getFinancialIndicators, getNetworkIndicators, getQualityIndicators, getSubscriberIndicators, getStatusFromCode } from 'rpqs/utils/RPQSUtils'
import NetworkDialog from './indicatorDialogs/network/NetworkDialog'
import QualityDialog from './indicatorDialogs/quality/QualityDialog'
import FinancialDialog from './indicatorDialogs/financial/FinancialDialog'
import { FINANCIAL, FINANCIAL_COLOR, GREY_BG, HOVER_COLOR_ROW, NETWORK, NETWORK_COLOR, QUALITY, QUALITY_COLOR, SUBSCRIBER, SUBSCRIBER_COLOR } from 'rpqs/constants/RpqsConstant'
import { CardTitle } from 'components/card/NewCard'
import Search from '@mui/icons-material/Search'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import SelectChipIndicator from './SelectChipIndicator'
import { normalizeString } from 'utils/StringUtil'
import IndicatorCategoryCard from '../IndicatorCategoryCard'
import { rpqsIndicatorShape } from 'rpqs/constants/RpqsShapes'
import RpqsIndicatorDialog from './indicatorDialogs/RpqsIndicatorDialog'

const DISABLED_COLOR = 'rgba(0, 0, 0, 0.38)'

const CurrentValueCell = ({ value = '', notes = '' }) => (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <span>{value}</span>
        {notes ? (
            <Tooltip title={notes}>
                <StickyNote2Icon fontSize='small' color='action'/>
            </Tooltip>
        ) : <div/>}
    </Box>
)

CurrentValueCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    notes: PropTypes.string,
}

export const CategoryPanel = ({
    backIndicators = [],
    category = '',
    searchValue = '',
    rpqs = {},
    egId,
    progress,
    refValues = {},
    onSaved = () => {},
}) => {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [codeIndicatorPanel, setCodeIndicatorPanel] = useState('')
    const [dialogTitle, setDialogTitle] = useState('')
    const [branchedDialogOpen, setBranchedDialogOpen] = useState(false)
    const [branchedMainCode, setBranchedMainCode] = useState('')
    const [isCategoryHover, setIsCategoryHover] = useState(false)
    const [rowIndexHover, setRowIndexHover] = useState(-1)

    const backMainByCode = keyBy(backIndicators.filter(ind => !ind.parent), 'code')

    const handleClickRow = (row) => {
        if (row.isBranched) {
            setBranchedMainCode(row.code)
            setBranchedDialogOpen(true)
        } else {
            setCodeIndicatorPanel(row.code)
            setDialogTitle(`${row.indicator} - ${row.indicatorDenomination}`)
            setDialogOpen(true)
        }
    }

    const getCategoryElements = (categ) => {
        switch (categ) {
            case SUBSCRIBER:
                return {
                    indicators: getSubscriberIndicators(),
                    color: SUBSCRIBER_COLOR,
                    dialog: <SubscriberDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            case NETWORK:
                return {
                    indicators: getNetworkIndicators(),
                    color: NETWORK_COLOR,
                    dialog: <NetworkDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            case QUALITY:
                return {
                    indicators: getQualityIndicators(),
                    color: QUALITY_COLOR,
                    dialog: <QualityDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            case FINANCIAL:
                return {
                    indicators: getFinancialIndicators(),
                    color: FINANCIAL_COLOR,
                    dialog: <FinancialDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            default:
                return {}
        }
    }

    const { color, indicators, dialog } = getCategoryElements(category)

    const filteredIndicators = searchValue ?
        indicators.filter(indicatorObj => (
            normalizeString(indicatorObj.indicator).startsWith(searchValue) || normalizeString(i18n[indicatorObj.indicatorDenomination]).startsWith(searchValue)
        )) :
        indicators

    const indicatorRows = filteredIndicators.map((indicator) => {
        const backInd = backMainByCode[indicator.code]
        return {
            ...indicator,
            indicatorDenomination: backInd?.name || i18n[indicator.indicatorDenomination] || i18n.unknown,
            unit: backInd?.unit ?? indicator.unit,
            isBranched: !!backInd,
            value: formatIndicatorValue(backInd?.value, backInd?.fieldType),
            previousValue: formatIndicatorValue(backInd?.previousValue, backInd?.fieldType),
            status: backInd && getStatusFromCode(backInd.status),
            notes: backInd?.notes,
        }
    })

    return (
        <>
            {indicatorRows.map((row, i) => (
                <TableRow
                    key={row.code}
                    sx={{
                        '&:hover td': {
                            backgroundColor: i === rowIndexHover && !isCategoryHover ? HOVER_COLOR_ROW : 'transparent',
                        },
                        '& td:first-of-type': i === 0 && {
                            backgroundColor: 'transparent',
                            cursor: 'auto',
                        },
                        '& td': {
                            borderBottom: 'none',
                            color: row.isBranched ? undefined : DISABLED_COLOR,
                        },
                        borderBottom: i === indicatorRows.length - 1 ? '1px grey solid' : 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setRowIndexHover(i)}
                    onMouseLeave={() => setRowIndexHover(-1)}
                >
                    {/* Carte de catégorie sur la première ligne */}
                    {i === 0 &&
                        <TableCell
                            rowSpan={indicatorRows.length}
                            width={200}
                            sx={{ backgroundColor: 'white', color: 'white', cursor: 'auto', p: '8px' }}
                            onMouseEnter={() => setIsCategoryHover(true)}
                            onMouseLeave={() => setIsCategoryHover(false)}
                        >
                            <IndicatorCategoryCard
                                variant='secondary'
                                height={`calc(${indicatorRows.length}*40px)`}
                                card={{
                                    title: i18n[category],
                                    advencement: Math.round(progress ?? 0),
                                    status: 0,
                                    nbIndicators: 0,
                                    nbIndicatorsTotal: indicators.length,
                                    color,
                                }}
                            />
                        </TableCell>
                    }

                    <TableCell onClick={() => handleClickRow(row)} sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        {row.indicatorDenomination}
                    </TableCell>
                    <TableCell onClick={() => handleClickRow(row)}>
                        {row.indicator}
                    </TableCell>
                    <TableCell onClick={() => handleClickRow(row)} sx={{ py: 1, pr: 1 }}>
                        <Box sx={{ backgroundColor: GREY_BG, width: '100%', height: '100%', display: 'flex', alignItems: 'center', px: 1, borderRadius: 1 }}>
                            {row.previousValue}
                        </Box>
                    </TableCell>
                    <TableCell onClick={() => handleClickRow(row)}>
                        <CurrentValueCell value={row.value} notes={row.notes}/>
                    </TableCell>
                    <TableCell onClick={() => handleClickRow(row)}>
                        {row.unit}
                    </TableCell>
                    <TableCell width={200} sx={{ overflow: 'visible', cursor: 'auto' }}>
                        {row.isBranched &&
                            <SelectChipIndicator indicatorStatus={row.status} readOnly/>
                        }
                    </TableCell>
                </TableRow>
            ))}

            {dialog}

            <RpqsIndicatorDialog
                open={branchedDialogOpen}
                setOpen={setBranchedDialogOpen}
                mainCode={branchedMainCode}
                indicators={backIndicators}
                rpqs={rpqs}
                egId={egId}
                refValues={refValues}
                onSaved={onSaved}
            />
        </>
    )
}

CategoryPanel.propTypes = {
    backIndicators: PropTypes.arrayOf(rpqsIndicatorShape),
    category: PropTypes.string,
    searchValue: PropTypes.string,
    rpqs: PropTypes.shape({ year: PropTypes.number, collectivityId: PropTypes.number }),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    progress: PropTypes.number,
    refValues: PropTypes.objectOf(PropTypes.number),
    onSaved: PropTypes.func,
}

export const IndicatorPanel = ({
    rpqs = {},
    egId,
    indicators = [],
    progress = {},
    refValues = {},
    onSaved = () => {},
}) => {
    const [searchValue, setSearchValue] = useState('')

    const handleSearch = (e) => {
        setSearchValue(e.target.value)
    }

    const year = rpqs.year
    const categories = [SUBSCRIBER, NETWORK, QUALITY, FINANCIAL]
    const progressByCategory = {
        [SUBSCRIBER]: progress.progressSubscriber,
        [NETWORK]: progress.progressNetwork,
        [QUALITY]: progress.progressQuality,
        [FINANCIAL]: progress.progressFinancial,
    }

    return (
        <Card>
            <CardTitle
                title={i18n.rpqsEdition}
                actions={[
                    // Search input
                    <Grid
                        container
                        key={'search'}
                        sx={{ position: 'relative', height: '36px' }}
                        py={'4px'}
                        direction={'column'}
                        justifyContent={'center'}
                    >
                        <input
                            placeholder={i18n.search}
                            className='no-margin'
                            onChange={handleSearch}
                        />
                        <Search
                            sx={{ position: 'absolute', right: 4 }}
                        />
                    </Grid>,
                ]}
            />
            <CardContent>
                {year &&
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{i18n.category}</TableCell>
                                <TableCell>{i18n.indicatorDenomination}</TableCell>
                                <TableCell>{i18n.indicator}</TableCell>
                                <TableCell>{`${year - 1}`}</TableCell>
                                <TableCell>{`${year}`}</TableCell>
                                <TableCell>{i18n.unit}</TableCell>
                                <TableCell>{i18n.status}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                '& tr:last-of-type': {
                                    borderBottom: 'none',
                                },
                            }}
                        >
                            {categories.map((category) => (
                                <CategoryPanel
                                    key={category}
                                    backIndicators={indicators}
                                    category={category}
                                    searchValue={searchValue}
                                    rpqs={rpqs}
                                    egId={egId}
                                    progress={progressByCategory[category]}
                                    refValues={refValues}
                                    onSaved={onSaved}
                                />
                            ))}
                        </TableBody>
                    </Table>
                }
            </CardContent>
        </Card>
    )
}

IndicatorPanel.propTypes = {
    rpqs: PropTypes.shape({ year: PropTypes.number, collectivityId: PropTypes.number }),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    indicators: PropTypes.arrayOf(rpqsIndicatorShape),
    progress: PropTypes.shape({
        progressSubscriber: PropTypes.number,
        progressNetwork: PropTypes.number,
        progressQuality: PropTypes.number,
        progressFinancial: PropTypes.number,
    }),
    refValues: PropTypes.objectOf(PropTypes.number),
    onSaved: PropTypes.func,
}